import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import gql from 'graphql-tag';
import debounce from 'lodash/debounce';
import { apolloClient } from 'lib/graphql';

export type WageAdvisorState = 'hidden' | 'loading' | 'displaying';

export type WageAdvisorBackendState = 'BELOW_MARKET' | 'WITHIN_MARKET' | 'ABOVE_MARKET';

export interface WageAdvisorOfficeLocation {
  city?: string | null;
  countryCode?: string | null;
}

export interface WageAdvisorInput {
  categoryId?: string | number | null;
  experienceLevel?: number | null;
  grossSalaryLow?: number | null;
  grossSalaryHigh?: number | null;
  currencyCode?: string | null;
  workModel?: string | null;
  officeLocations?: WageAdvisorOfficeLocation[];
  eligibleLocations?: string[];
  topGeographies?: string[];
  companyCountryCode?: string | null;
}

export interface WageAdvisorPayload {
  state: WageAdvisorBackendState;
  percentDifference: number;
  benchmarkMin: number;
  benchmarkMax: number;
  editionYear: number;
  reportUrl: string;
  country: string;
  role: string;
  experienceLevelLabel: string;
}

interface WageAdvisorQueryResult {
  job: {
    wageAdvisor: WageAdvisorPayload | null;
  };
}

export const WAGE_ADVISOR_QUERY = gql`
  query WageAdvisor(
    $categoryId: ID!
    $experienceLevel: Int!
    $grossSalaryLow: Int!
    $grossSalaryHigh: Int!
    $currencyCode: String!
    $workModel: String!
    $officeLocations: [WageAdvisorOfficeLocationInput!]
    $eligibleLocations: [String!]
    $topGeographies: [String!]
    $companyCountryCode: String
  ) {
    job {
      wageAdvisor(
        categoryId: $categoryId
        experienceLevel: $experienceLevel
        grossSalaryLow: $grossSalaryLow
        grossSalaryHigh: $grossSalaryHigh
        currencyCode: $currencyCode
        workModel: $workModel
        officeLocations: $officeLocations
        eligibleLocations: $eligibleLocations
        topGeographies: $topGeographies
        companyCountryCode: $companyCountryCode
      ) {
        state
        percentDifference
        benchmarkMin
        benchmarkMax
        editionYear
        reportUrl
        country
        role
        experienceLevelLabel
      }
    }
  }
`;

export const DEBOUNCE_MS = 500;

function isComplete(input: WageAdvisorInput): boolean {
  const {
    categoryId,
    experienceLevel,
    grossSalaryLow,
    grossSalaryHigh,
    currencyCode,
    workModel,
  } = input;

  if (categoryId === null || categoryId === undefined || categoryId === '') return false;
  if (experienceLevel === null || experienceLevel === undefined) return false;
  if (!grossSalaryLow || grossSalaryLow <= 0) return false;
  if (!grossSalaryHigh || grossSalaryHigh <= 0) return false;
  if (grossSalaryLow > grossSalaryHigh) return false;
  if (!currencyCode) return false;
  if (!workModel) return false;

  return true;
}

function toVariables(input: WageAdvisorInput) {
  return {
    categoryId: String(input.categoryId),
    experienceLevel: Number(input.experienceLevel),
    grossSalaryLow: Number(input.grossSalaryLow),
    grossSalaryHigh: Number(input.grossSalaryHigh),
    currencyCode: input.currencyCode,
    workModel: input.workModel,
    officeLocations: input.officeLocations || [],
    eligibleLocations: input.eligibleLocations || [],
    topGeographies: input.topGeographies || [],
    companyCountryCode: input.companyCountryCode || null,
  };
}

export interface UseWageAdvisorResult {
  state: WageAdvisorState;
  data: WageAdvisorPayload | null;
}

export function useWageAdvisor(input: WageAdvisorInput): UseWageAdvisorResult {
  const [lastData, setLastData] = useState<WageAdvisorPayload | null>(null);
  const [errored, setErrored] = useState(false);
  const complete = isComplete(input);

  const fetchWageAdvisor = useCallback(async (variables: ReturnType<typeof toVariables>) => {
    try {
      const result = await apolloClient.query<WageAdvisorQueryResult>({
        query: WAGE_ADVISOR_QUERY,
        variables,
        fetchPolicy: 'no-cache',
      });
      const payload = result.data?.job?.wageAdvisor;
      if (payload) {
        setErrored(false);
        setLastData(payload);
      } else {
        setErrored(true);
        setLastData(null);
      }
    } catch (_e) {
      setErrored(true);
      setLastData(null);
    }
  }, []);

  const debouncedFetch = useMemo(
    () => debounce((variables: ReturnType<typeof toVariables>) => {
      fetchWageAdvisor(variables);
    }, DEBOUNCE_MS),
    [fetchWageAdvisor]
  );

  const variablesKey = useMemo(() => {
    if (!complete) return null;
    return JSON.stringify(toVariables(input));
  }, [complete, input]);

  const previousKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!complete) {
      debouncedFetch.cancel();
      setErrored(false);
      setLastData(null);
      previousKeyRef.current = null;
      return;
    }

    if (variablesKey === previousKeyRef.current) return;
    previousKeyRef.current = variablesKey;

    // Keep the previously displayed state visible until the new response
    // arrives, so transitions never flash an empty placeholder.
    debouncedFetch(toVariables(input));
  }, [complete, variablesKey, debouncedFetch, input]);

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  let state: WageAdvisorState;
  if (!complete) {
    state = 'hidden';
  } else if (errored && !lastData) {
    state = 'hidden';
  } else if (lastData) {
    state = 'displaying';
  } else {
    state = 'loading';
  }

  return { state, data: lastData };
}
